import { useState } from 'react';
import { Upload } from 'lucide-react';
import type { OrderImportPreviewResponse, OrderImportResponse } from '@sourcito/schemas';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FormField,
} from '../design';
import { ApiCallError } from '../lib/api';
import { previewOrderImport } from '../lib/order-planning';
import { useSuppliers } from '../lib/suppliers';
import { OrderImportResolveTable } from './OrderImportResolveTable';

const ACCEPTED_EXTENSIONS = /\.(xlsx|xls|csv)$/i;
const MAX_BYTES = 10 * 1024 * 1024;

export interface OrderImportFormProps {
  /** Called with the parsed preview once a supplier order file resolves
   *  successfully — the resolve table (Task 9) below renders off the
   *  form's own `preview` state regardless; this is only for a caller that
   *  wants the raw preview too. */
  onPreview?: (result: OrderImportPreviewResponse) => void;
  /** Called once the resolve table's "Create plan" succeeds. The form
   *  closes itself (via `onCancel`) right after — something has now been
   *  committed, unlike the preview step, so there's nothing left to keep
   *  the dialog open for. A caller (e.g. the planning page) uses this to
   *  invalidate its plans list / navigate to the new plan. */
  onImported?: (result: OrderImportResponse) => void;
  /** Dismiss affordance — a plain callback rather than the design system's
   *  DialogClose so this component renders (and is testable) without a
   *  surrounding Dialog/Portal. OrderImportModal below wires it to close. */
  onCancel?: () => void;
}

/**
 * Upload → preview body for order import (2026-09-21 design, Task 8).
 * Exported separately from OrderImportModal so it can be rendered directly
 * in tests: DialogContent's Radix Portal needs a real `document`, which
 * this repo's node-environment test setup doesn't have (see
 * PlanningPage.test.tsx's own react-test-renderer convention — no jsdom in
 * this project).
 *
 * Deliberately does not clear `preview` or otherwise reset itself after a
 * successful call, unlike PricingSheetUploadModal's upload-then-close — the
 * whole point of a preview is that nothing is committed yet, so the human
 * needs to keep looking at the result (and, from Task 9, resolve it) before
 * anything happens. `onCancel` is this component's only exit; what it does
 * (close, reset, both) is entirely up to the caller — see OrderImportModal
 * below.
 */
export function OrderImportForm({ onPreview, onImported, onCancel }: OrderImportFormProps) {
  const suppliersQuery = useSuppliers();
  const suppliers = suppliersQuery.data ?? [];
  const [supplierId, setSupplierId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState<OrderImportPreviewResponse | null>(null);

  function validate(f: File): string | null {
    if (!ACCEPTED_EXTENSIONS.test(f.name)) {
      return 'File must be .xlsx, .xls, or .csv.';
    }
    if (f.size === 0) return 'File is empty.';
    if (f.size > MAX_BYTES) {
      return `File exceeds the ${Math.round(MAX_BYTES / 1024 / 1024)} MB upload limit.`;
    }
    return null;
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setPreview(null);
    const picked = e.target.files?.[0] ?? null;
    if (!picked) {
      setFile(null);
      setClientError(null);
      return;
    }
    const err = validate(picked);
    if (err) {
      setFile(null);
      setClientError(err);
      return;
    }
    setFile(picked);
    setClientError(null);
  }

  async function handleSubmit() {
    if (!supplierId) {
      setClientError('Pick a supplier.');
      return;
    }
    if (!file) {
      setClientError('Choose a file.');
      return;
    }
    setClientError(null);
    setSubmitting(true);
    try {
      const result = await previewOrderImport({ supplierId, file });
      setPreview(result);
      onPreview?.(result);
    } catch (err) {
      if (err instanceof ApiCallError) {
        setClientError(err.message);
      } else {
        setClientError((err as Error).message ?? 'Import preview failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <FormField label="Supplier" htmlFor="order-import-supplier" required>
        <select
          id="order-import-supplier"
          value={supplierId}
          onChange={(e) => setSupplierId(e.target.value)}
          disabled={suppliersQuery.isLoading}
          className="w-full rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm text-fg-primary"
        >
          <option value="">Select a supplier…</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </FormField>

      <FormField label="File" htmlFor="order-import-file" required>
        <input
          id="order-import-file"
          type="file"
          accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          onChange={handleFileChange}
          className="block w-full text-sm text-fg-secondary file:mr-3 file:rounded-sm file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-fg-primary hover:file:bg-surface-3"
        />
        {/* Load-bearing: this modal looks almost identical to the pricing-
            sheet upload, and that upload replaces the supplier's entire
            price catalogue. Do not drop or reword this sentence. */}
        <p className="mt-2 text-xs text-fg-tertiary">
          Creates a draft plan from this order. Does not change your price file.
        </p>
      </FormField>

      {file && (
        <p className="text-xs text-fg-tertiary">
          <span className="font-mono">{file.name}</span> · {Math.ceil(file.size / 1024)} KB
        </p>
      )}

      {clientError && <p className="text-sm text-negative">{clientError}</p>}

      {preview && (
        <OrderImportResolveTable
          preview={preview}
          onImported={(result) => {
            onImported?.(result);
            onCancel?.();
          }}
        />
      )}

      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={submitting} disabled={submitting} size="sm">
          Preview
        </Button>
      </DialogFooter>
    </div>
  );
}

export interface OrderImportModalProps {
  /** See OrderImportFormProps.onPreview — forwarded through untouched. */
  onPreview?: (result: OrderImportPreviewResponse) => void;
  /** See OrderImportFormProps.onImported — forwarded through untouched. */
  onImported?: (result: OrderImportResponse) => void;
}

/**
 * Import entry-point (Task 8): the trigger button + Dialog chrome around
 * OrderImportForm above. Closely mirrors PricingSheetUploadModal's
 * open-state handling, except it never calls setOpen(false) on a
 * successful preview — the whole point of a preview is that nothing is
 * committed yet, so the dialog stays open showing the result (Task 9's
 * resolve step extends this same open dialog rather than a fresh one).
 * Radix unmounts DialogContent's subtree when closed, so OrderImportForm's
 * own state (supplier, file, preview) resets on the next open regardless —
 * only the "don't auto-close on success" behavior is deliberate here.
 */
export function OrderImportModal({ onPreview, onImported }: OrderImportModalProps) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" leadingIcon={<Upload className="size-4" />}>
          Import order
        </Button>
      </DialogTrigger>
      {/* The resolve table is a 7-column grid with a 720px minimum, and an
          order can run to dozens of rows. The Dialog primitive defaults to
          max-w-md (448px) with no height cap, which clipped the line-total
          column off the right edge and ran the dialog off the bottom of the
          viewport with nothing to scroll. Widen it and let the body scroll
          inside the dialog instead of the page. */}
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import order</DialogTitle>
          <DialogDescription>
            .xlsx, .xls, or .csv up to 10 MB. We match each line to an Amazon ASIN by UPC.
          </DialogDescription>
        </DialogHeader>
        <OrderImportForm
          onPreview={onPreview}
          onImported={onImported}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
